import {
  NotificationPreference,
  NotificationPreferencesProps,
  NotificationPreferencesSection,
} from '@ecosystem/newron-client-lib';
import { WithMultiSelect } from '@components/design';
import React, { useEffect, useMemo, useState } from 'react';
import { useFetchEntitledMarkets } from './hooks/UseFetchEntitledMarkets';
import { getSelectedMarkets } from './utils/GetSelectedMarkets';

export const RetailUpdates: React.FC<NotificationPreferencesProps> = (props: NotificationPreferencesProps) => {
  const { entitledMarkets = [] } = useFetchEntitledMarkets();
  const [options, setOptions] = useState<{ value: string; label: string }[]>([]);
  const [filteredPreferences, setFilteredPreferences] = useState<NotificationPreference>({
    type: '',
    settings: { email: false, push: false },
    context: {} as any,
  });
  const selectedMarkets: string[] = useMemo(
    () => filteredPreferences?.context?.markets?.map(({ countryId }: { countryId: string }) => `${countryId}`),
    [filteredPreferences],
  );

  useEffect(() => {
    setOptions(
      entitledMarkets.map((market) => ({
        value: market.id,
        label: market.name,
      })),
    );
  }, [entitledMarkets]);

  useEffect(() => {
    if (entitledMarkets?.length) {
      setFilteredPreferences(props.preferences);
    }
  }, [entitledMarkets, props.preferences]);

  const onSelectHandler = (selectedOptions: string | string[] | null) => {
    let updatedPreferences = { ...filteredPreferences };

    updatedPreferences = {
      type: 'retail-updates',
      settings: {
        email: filteredPreferences?.settings?.email ?? false,
        push: filteredPreferences?.settings?.push ?? false,
      },
      context: {
        markets: getSelectedMarkets(selectedOptions, entitledMarkets),
      },
    };
    props.onChange(updatedPreferences, isValid(updatedPreferences), hasDataLoadingError());
    setFilteredPreferences(updatedPreferences);
  };

  const hasDataLoadingError = (): boolean => {
    return options.length === 0;
  };

  const isValid = (preferences: NotificationPreference): boolean => {
    return (
      (Object.values(preferences?.settings).some((setting) => setting) && !!preferences?.context?.markets?.length) ||
      Object.values(preferences?.settings).every((setting) => !setting)
    );
  };

  const noSettingsSelected = Object.values(props?.preferences?.settings).every((setting) => !setting);

  return (
    <NotificationPreferencesSection
      title="gfknewron Retail"
      type="retail-updates"
      email
      push
      preferences={filteredPreferences}
      onChange={(prefs) => props.onChange(prefs, isValid(prefs), hasDataLoadingError())}
      checkBoxTestId="retail-updates"
    >
      <div key={`${options}-${noSettingsSelected}`}>
        <WithMultiSelect
          options={options}
          placeholder={'Choose markets'}
          label="Which areas should we notify you about?"
          values={selectedMarkets}
          name="retail-select"
          {...(!options.length && { innerHelperText: 'Data has not been loaded' })}
          {...(props.showError && { errorMessage: 'Please select at least one market to save these settings.' })}
          disabled={noSettingsSelected}
          selectionType="checkbox"
          onChange={onSelectHandler}
        />
      </div>
    </NotificationPreferencesSection>
  );
};
